import { useEffect, useRef, useState, useCallback } from "react";
import api, { BACKEND_URL } from "@/lib/api";
import { Volume2, VolumeX, SkipForward, Play } from "lucide-react";

export default function MusicPlayer() {
  const [playlist, setPlaylist] = useState([]);
  const [idx, setIdx] = useState(0);
  const [muted, setMuted] = useState(true);
  const [started, setStarted] = useState(false);
  const audioRef = useRef(null);

  // Load playlist (poll every 30s so admin changes show up)
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get("/music/playlist");
        if (!mounted) return;
        setPlaylist((cur) => {
          const same = cur.length === data.length && cur.every((t, i) => t.id === data[i]?.id);
          return same ? cur : data;
        });
      } catch {}
    };
    load();
    const i = setInterval(load, 30000);
    return () => { mounted = false; clearInterval(i); };
  }, []);

  // Load current track src whenever idx or playlist changes
  useEffect(() => {
    const a = audioRef.current;
    if (!a || playlist.length === 0) return;
    const track = playlist[idx % playlist.length];
    if (!track) return;
    a.src = `${BACKEND_URL}${track.url}`;
    a.loop = false;
    if (started) {
      a.volume = 0.45;
      a.muted = false;
      a.play().catch(() => {});
    }
  }, [idx, playlist, started]);

  const handleEnded = useCallback(() => {
    setIdx((i) => (playlist.length ? (i + 1) % playlist.length : 0));
  }, [playlist.length]);

  // The big start button — direct user gesture, satisfies every browser
  const startPlayback = async () => {
    const a = audioRef.current;
    if (!a || playlist.length === 0) return;
    try {
      a.muted = false;
      a.volume = 0.45;
      await a.play();
      setStarted(true);
      setMuted(false);
    } catch (e) {
      // Last-ditch fallback for stubborn iOS — try muted first then unmute on a tick
      try {
        a.muted = true;
        await a.play();
        setTimeout(() => { a.muted = false; setMuted(false); setStarted(true); }, 50);
      } catch {}
    }
  };

  const toggleMute = () => {
    const a = audioRef.current;
    if (!a) return;
    a.muted = !a.muted;
    setMuted(a.muted);
    if (!a.muted && a.paused) a.play().catch(() => {});
  };

  const skip = () => setIdx((i) => (playlist.length ? (i + 1) % playlist.length : 0));

  // ANTI-RECORD DETERRENT: pause when the tab loses focus (which happens
  // the moment iOS / macOS QuickTime starts a screen recording, and when
  // the user switches to any recording app). Also refresh the playlist so
  // signed URLs stay valid.
  useEffect(() => {
    const onVis = () => {
      const a = audioRef.current;
      if (!a) return;
      if (document.hidden) {
        try { a.pause(); } catch { /* ignore */ }
      } else if (started && !muted) {
        // On return, top up signed URLs by re-fetching playlist
        api.get("/music/playlist").then(({ data }) => {
          setPlaylist(data);
          setTimeout(() => { try { a.play(); } catch { /* ignore */ } }, 50);
        }).catch(() => {
          try { a.play(); } catch { /* ignore */ }
        });
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [started, muted]);

  if (playlist.length === 0) return null;
  const current = playlist[idx % playlist.length];

  // BEFORE the user has started — show a prominent start button
  if (!started) {
    return (
      <>
        <audio
          ref={audioRef}
          preload="auto"
          playsInline
          onEnded={handleEnded}
          onContextMenu={(e) => e.preventDefault()}
          controlsList="nodownload noremoteplayback noplaybackrate"
          disableRemotePlayback
        />
        <button
          onClick={startPlayback}
          data-testid="music-start-btn"
          className="fixed bottom-5 left-5 z-50 group flex items-center gap-3 px-5 py-3 bg-cyan-400 text-black rounded-full font-display font-bold uppercase tracking-[0.3em] text-xs shadow-[0_0_30px_rgba(0,229,255,0.5)] hover:scale-105 transition-transform"
          aria-label="start music"
        >
          <span className="relative flex">
            <span className="absolute inline-flex h-full w-full rounded-full bg-cyan-300 opacity-70 animate-ping" />
            <Play size={14} className="relative" fill="currentColor" />
          </span>
          <span>Tap For Sound</span>
        </button>
      </>
    );
  }

  // AFTER playback started — small chip with music credit
  return (
    <div className="fixed bottom-5 left-5 z-50 glass rounded-full pl-3 pr-4 py-2 flex items-center gap-2" data-testid="music-player">
      <audio
        ref={audioRef}
        preload="auto"
        playsInline
        onEnded={handleEnded}
        onContextMenu={(e) => e.preventDefault()}
        controlsList="nodownload noremoteplayback noplaybackrate"
        disableRemotePlayback
      />
      <button onClick={toggleMute} className="text-white/80 hover:text-cyan-400 transition" data-testid="music-mute-toggle" aria-label="toggle sound">
        {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
      </button>
      {playlist.length > 1 && (
        <button onClick={skip} className="text-white/80 hover:text-cyan-400 transition" data-testid="music-skip" aria-label="next track">
          <SkipForward size={16} />
        </button>
      )}
      <span className="text-[10px] uppercase tracking-widest text-white/60 hidden sm:inline truncate max-w-[180px]">
        {current?.title}{playlist.length > 1 ? ` · ${(idx % playlist.length) + 1}/${playlist.length}` : ""}
      </span>
      <a
        href="https://instagram.com/z4n4r4"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="music-credit"
        title="Music by @z4n4r4"
        className="text-[9px] uppercase tracking-[0.25em] text-fuchsia-400/80 hover:text-fuchsia-400 transition border-l border-white/10 pl-2 ml-1 hidden sm:inline"
      >
        ♪ @z4n4r4
      </a>
    </div>
  );
}
